import { Body, Controller, Delete, Get, Param, Patch, Post, Put, UseGuards, ValidationPipe } from '@nestjs/common';
import { AuthGuard } from '@nestjs/passport';
import { ApiBearerAuth, ApiBody, ApiParam, ApiTags } from '@nestjs/swagger';
import { RequireModule } from 'src/auth/decorators/require-module.decorator';
import { RequirePermission } from 'src/auth/decorators/require-permission.decorator';
import { ModuleGuard } from 'src/auth/guards/module.guard';
import { PermissionGuard } from 'src/auth/guards/permission.guard';
import type { LoginUserInfo } from '../auth/dto/login.dto';
import { GetUser } from '../auth/get-user.decorator';
import { CreateMarcaDto } from './dto/create-marca.dto';
import { UpdateMarcaDto } from './dto/update-marca.dto';
import { MarcasService } from './marcas.service';

@ApiTags('Marcas')
@ApiBearerAuth()
@UseGuards(AuthGuard('jwt'), ModuleGuard)
@RequireModule('INVENTARIO')
@Controller({ version: '1', path: 'marcas' })
export class MarcasController {
  constructor(private readonly marcasService: MarcasService) {}

  @Post()
  @UseGuards(PermissionGuard)
  @RequirePermission('INVENTARIO', 'INV_MAR_MARCA_CREAR')
  @ApiBody({ type: CreateMarcaDto })
  async create(@Body(ValidationPipe) dto: CreateMarcaDto, @GetUser() user: LoginUserInfo) {
    return this.marcasService.create(dto, user.empresa_id, user.id);
  }

  @Get()
  @UseGuards(PermissionGuard)
  @RequirePermission('INVENTARIO', 'INV_MAR_MARCA_VER')
  async findAll(@GetUser() user: LoginUserInfo) {
    return this.marcasService.findAll(user.empresa_id);
  }

  @Get(':id')
  @UseGuards(PermissionGuard)
  @RequirePermission('INVENTARIO', 'INV_MAR_MARCA_VER')
  @ApiParam({ name: 'id', description: 'ID de la marca' })
  async findOne(@Param('id') id: string) {
    return this.marcasService.findOne(id);
  }

  @Put(':id')
  @UseGuards(PermissionGuard)
  @RequirePermission('INVENTARIO', 'INV_MAR_MARCA_EDITAR')
  @ApiParam({ name: 'id', description: 'ID de la marca' })
  @ApiBody({ type: UpdateMarcaDto })
  async update(@Param('id') id: string, @Body(ValidationPipe) dto: UpdateMarcaDto, @GetUser() user: LoginUserInfo) {
    return this.marcasService.update(id, dto, user.empresa_id, user.id);
  }

  @Patch(':id/toggle')
  @UseGuards(PermissionGuard)
  @RequirePermission('INVENTARIO', 'INV_MAR_MARCA_EDITAR')
  @ApiParam({ name: 'id', description: 'ID de la marca' })
  async toggleActive(@Param('id') id: string, @GetUser() user: LoginUserInfo) {
    return this.marcasService.toggleActive(id, user.empresa_id, user.id);
  }

  @Delete(':id')
  @UseGuards(PermissionGuard)
  @RequirePermission('INVENTARIO', 'INV_MAR_MARCA_ELIMINAR')
  @ApiParam({ name: 'id', description: 'ID de la marca' })
  async remove(@Param('id') id: string, @GetUser() user: LoginUserInfo) {
    return this.marcasService.remove(id, user.empresa_id, user.id);
  }
}
